"use client";

import Image from "next/image";
import { motion } from "framer-motion";
import EditableElement from "@/components/customize/EditableElement";

const fadeIn = {
  hidden: { opacity: 0, y: 50 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 1,
      ease: "easeOut",
    },
  },
};

const imageZoom = {
  hidden: { scale: 0.8, opacity: 0 },
  visible: {
    scale: 1,
    opacity: 1,
    transition: {
      duration: 1,
      ease: "easeOut",
    },
  },
};

export default function Couple({
  bridegroom = [],
  templateMeta,
  __isMobile = false,
  __isEditable = false,
  __sectionId,
  __onFieldEdit,
  __onFieldChange,
  __onFileSelected,
  __focusFieldName,
}) {
  if (!bridegroom.length) return null;

  const safeBridegroom = bridegroom.filter((person) => Boolean(person));

  return (
    <div className="flex flex-col py-12 bg-white items-center gap-16">
      {safeBridegroom.map((person, index) => (
        <motion.div
          key={index}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.4 }}
          variants={fadeIn}
          className="flex flex-col items-center text-center px-4"
        >
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.5 }}
            variants={imageZoom}
            className="w-75 h-100 relative shadow-2xl mb-6 will-change-transform"
          >
            <EditableElement
              type="image"
              value={person.image}
              onChange={(val) => __onFieldChange?.(__sectionId, `bridegroom[${index}].image`, val)}
              isEditable={__isEditable}
              fieldName={`bridegroom[${index}].image`}
              onFileSelected={(file, url) => __onFileSelected?.(`defaultSections.${__sectionId}.props.bridegroom[${index}].image`, file, url)}
              className="absolute inset-0 w-full h-full"
            >
              {typeof person.image === "string" && person.image.trim() !== "" && (
                <Image
                  src={person.image}
                  alt={person.name || "Couple"}
                  fill
                  className="object-cover"
                  sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
                />
              )}
            </EditableElement>
          </motion.div>
          <EditableElement
            type="text"
            value={person.role}
            onChange={(val) => __onFieldChange?.(__sectionId, `bridegroom[${index}].role`, val)}
            isEditable={__isEditable}
            fieldName={`bridegroom[${index}].role`}
            onEditStart={() => __onFieldEdit?.(__sectionId, `bridegroom[${index}].role`)}
            isActive={__focusFieldName === `bridegroom[${index}].role`}
            isMobile={__isMobile}
          >
            <p className="text-sm uppercase mb-2 tracking-widest text-black font-sans">
              {person.role}
            </p>
          </EditableElement>
          <EditableElement
            type="text"
            value={index === 0 ? "Groom" : "Bride"}
            isEditable={__isEditable}
            fieldName={index === 0 ? "groomFirstName" : "brideFirstName"}
            onEditStart={() => __onFieldEdit?.("general", index === 0 ? "groomFirstName" : "brideFirstName")}
            useContentEditable={false}
            className="mb-4"
          >
            <h2 className="text-4xl font-cormorant font-bold text-black">
              {index === 0 
                ? `${templateMeta?.groomFirstName || ""} ${templateMeta?.groomLastName || ""}`.trim() || "Groom"
                : `${templateMeta?.brideFirstName || ""} ${templateMeta?.brideLastName || ""}`.trim() || "Bride"}
            </h2>
          </EditableElement>
          {person.description && (
            <EditableElement
              type="text"
              value={person.description}
              onChange={(val) => __onFieldChange?.(__sectionId, `bridegroom[${index}].description`, val)}
              isEditable={__isEditable}
              fieldName={`bridegroom[${index}].description`}
              onEditStart={() => __onFieldEdit?.(__sectionId, `bridegroom[${index}].description`)}
              isActive={__focusFieldName === `bridegroom[${index}].description`}
              isMobile={__isMobile}
              useContentEditable={true}
              multiline={true}
            >
              <p className="text-black text-xs max-w-sm">{person.description}</p>
            </EditableElement>
          )}
        </motion.div>
      ))}
    </div>
  );
}
